import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Package,
  Users,
  CreditCard,
  Calendar,
  Clock,
  MessageSquare,
  ClipboardCheck,
  Settings as Cog,
  LogOut,
  Box,
  Menu,
  X,
  ExternalLink,
  Monitor,
  Globe,
  Building2,
  UserCog,
} from "lucide-react";
import { openKiosk } from "@/lib/openKiosk";
import { isAttendanceAvailable } from "@/lib/utils-app";
import TrialPeriodBadge from "@/components/TrialPeriodBadge";
import SubscriptionPaymentBanner from "@/components/SubscriptionPaymentBanner";

const baseItems = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/productos", label: "Productos", icon: Box },
  { to: "/admin/paquetes", label: "Paquetes", icon: Package },
  { to: "/admin/suscriptores", label: "Suscriptores", icon: Users },
  { to: "/admin/turnos", label: "Turnos", icon: Clock },
  { to: "/admin/asistencia", label: "Asistencia", icon: ClipboardCheck, attendanceOnly: true },
  { to: "/admin/pagos", label: "Pagos", icon: CreditCard },
  { to: "/admin/eventos", label: "Eventos", icon: Calendar },
  { to: "/admin/mensajes", label: "Mensajes", icon: MessageSquare },
  { to: "/admin/sitio-publico", label: "Sitio público", icon: Globe },
  { to: "/admin/configuracion", label: "Configuración", icon: Cog },
];

const masterItems = (branchAdminsAllowed) => [
  { to: "/admin/sucursales", label: "Sucursales", icon: Building2 },
  ...(branchAdminsAllowed
    ? [{ to: "/admin/usuarios-sucursal", label: "Usuarios de sucursal", icon: UserCog }]
    : []),
];

export default function AdminLayout() {
  const { logout, user } = useAuth();
  const { isMaster, branches, activeBranchId, activeBranch, activeBranchName, setActiveBranch } = useBranch();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openingKiosk, setOpeningKiosk] = useState(false);
  const [settings, setSettings] = useState({ gym_name: "BOXING GYM" });

  const loadBranchSettings = () => {
    api.get("/admin/settings").then((r) => setSettings(r.data || {})).catch(() => {});
  };

  useEffect(() => {
    loadBranchSettings();
    const reload = () => loadBranchSettings();
    window.addEventListener("branch-changed", reload);
    window.addEventListener("app-settings-updated", reload);
    return () => {
      window.removeEventListener("branch-changed", reload);
      window.removeEventListener("app-settings-updated", reload);
    };
  }, [activeBranchId]);

  const attendanceOn = isAttendanceAvailable(settings);

  const handleOpenKiosk = () => {
    setOpeningKiosk(true);
    openKiosk();
    toast.success("Kiosk abierto en nueva pestaña");
    setOpeningKiosk(false);
  };
  const branchAdminsAllowed = user?.branch_admins_allowed !== false;
  const items = [
    ...(isMaster ? masterItems(branchAdminsAllowed) : []),
    ...baseItems.filter((it) => !it.attendanceOnly || attendanceOn),
  ];
  const gymName = settings.gym_name || "BOXING GYM";
  const logo = settings.logo || "";
  const logoFallback = gymName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "BG";
  const userLabel = user?.username || user?.name || user?.email?.split("@")[0] || "Usuario";
  const branchLabel =
    activeBranchName ||
    activeBranch?.name ||
    user?.branch?.name ||
    branches.find((b) => b.id === (activeBranchId || user?.branch_id))?.name ||
    "";

  const doLogout = () => {
    logout();
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 fixed md:static z-40 inset-y-0 left-0 w-64 bg-[#0F0F0F] border-r border-theme-secondary/10 transition-transform flex flex-col`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 px-5 flex items-center gap-2 border-b border-theme-secondary/10">
          <div className="h-9 w-9 grid place-items-center bg-[#E63946] text-black font-display text-xl rounded-sm overflow-hidden">
            {logo ? (
              <img src={logo} alt="Logo del gimnasio" className="h-full w-full object-cover" />
            ) : (
              logoFallback
            )}
          </div>
          <div className="min-w-0">
            <div className="font-display text-xl text-white leading-none truncate" title={gymName}>
              {gymName}
            </div>
            <div
              className="text-[13px] text-white/60 truncate leading-tight mt-0.5"
              title={branchLabel ? `${userLabel} ${branchLabel}` : userLabel}
            >
              <span className="text-white/60">{userLabel}</span>
              {branchLabel ? <span className="text-white/60"> {branchLabel}</span> : null}
            </div>
          </div>
        </div>
        <nav className="p-4 space-y-1 flex-1 overflow-y-auto min-h-0">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`sidebar-${it.label.toLowerCase()}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm transition-all relative ${
                  isActive
                    ? "bg-white/5 text-white before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:bg-[#E63946]"
                    : "text-white/60 hover:text-white hover:bg-white/[0.03]"
                }`
              }
            >
              <it.icon size={16} /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="shrink-0 p-4 border-t border-theme-secondary/10 bg-[#0F0F0F] relative z-10">
          <div className="text-xs text-white/40 truncate">{user?.email}</div>
          {attendanceOn && (
            <button
              type="button"
              onClick={handleOpenKiosk}
              disabled={openingKiosk}
              className="btn-primary w-full justify-center mt-3 text-xs py-2 bg-green-700 hover:bg-green-600"
              data-testid="sidebar-open-kiosk-btn"
            >
              <Monitor size={14} /> {openingKiosk ? "Abriendo…" : "Abrir kiosk"}
            </button>
          )}
          <a href="/" target="_blank" rel="noreferrer" className="btn-primary w-full justify-center mt-3 text-xs py-2" data-testid="go-store-btn">
            <ExternalLink size={14} /> Ir a la tienda
          </a>
          <button onClick={doLogout} className="btn-outline w-full justify-center mt-3 text-xs py-2" data-testid="logout-btn">
            <LogOut size={14} /> Salir
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-theme-secondary/10 px-4 md:px-8 flex items-center justify-between bg-[#0A0A0A]">
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white" data-testid="sidebar-toggle">
            {mobileOpen ? <X /> : <Menu />}
          </button>
          <div className="flex items-center gap-3 min-w-0">
            <div className="text-sm text-white/60 hidden sm:block">Panel de administración</div>
            <TrialPeriodBadge subscription={user?.subscription} />
            {isMaster && branches.length > 0 && (
              <select
                className="input-dark text-sm py-1.5 max-w-[220px]"
                value={activeBranchId || ""}
                onChange={(e) => setActiveBranch(e.target.value)}
                data-testid="branch-selector"
              >
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            )}
            {!isMaster && activeBranch && (
              <span className="text-xs text-white/60 truncate" title={activeBranch.name}>
                {activeBranch.name}
              </span>
            )}
          </div>
          <div className="text-xs text-white/40">{new Date().toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long" })}</div>
        </header>
        <main className="flex-1 p-4 md:p-8">
          <SubscriptionPaymentBanner />
          <Outlet key={activeBranchId} />
        </main>
      </div>
      {mobileOpen && <div className="fixed inset-0 bg-black/60 md:hidden z-30" onClick={() => setMobileOpen(false)} />}
    </div>
  );
}
